import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	// The generic fallback copy lives in `common` (always loaded), so an
	// unexpected error renders localized even when the failing route's own
	// namespace never loaded (ADR-074).
	const { t } = useTranslation()
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

	// A generic, localized message — the raw error is captured for Sentry above,
	// never shown to the visitor. Callers can still pass a bespoke handler.
	const renderUnexpected =
		unexpectedErrorHandler ??
		(() => (
			<div className="flex flex-col items-center gap-2 text-center">
				<p>{t('error.title')}</p>
				<p className="text-body-md text-muted-foreground">{t('error.body')}</p>
			</div>
		))

	return (
		<div className="text-h2 container flex items-center justify-center p-20">
			{isResponse
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: renderUnexpected(error)}
		</div>
	)
}
